import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface MediaItem {
  id: number;
  type: 'award' | 'publication' | 'feature';
  title: string;
  source: string;
  date: string;
  description: string;
  image: string;
  alt: string;
}

interface MediaCoverageProps {
  mediaItems: MediaItem[];
}

export default function MediaCoverage({ mediaItems }: MediaCoverageProps) {
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'award':
        return 'TrophyIcon';
      case 'publication':
        return 'NewspaperIcon';
      case 'feature':
        return 'StarIcon';
      default:
        return 'DocumentTextIcon';
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'award':
        return 'bg-accent text-accent-foreground';
      case 'publication':
        return 'bg-secondary text-secondary-foreground';
      case 'feature':
        return 'bg-success text-success-foreground';
      default:
        return 'bg-muted text-textSecondary';
    }
  };

  return (
    <section className="bg-background py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-headline font-bold text-primary mb-4">
            Recognition & Media Coverage
          </h2>
          <p className="text-lg text-textSecondary max-w-3xl mx-auto">
            Industry recognition, thought leadership, and media features highlighting our commitment to excellence
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {mediaItems.map((item) => (
            <div
              key={item.id}
              className="bg-card rounded-lg shadow-subtle overflow-hidden hover:shadow-modal transition-shadow duration-300"
            >
              <div className="relative h-48 overflow-hidden bg-surface">
                <AppImage
                  src={item.image}
                  alt={item.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 right-4">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-body font-semibold ${getTypeBadge(
                      item.type
                    )}`}
                  >
                    <Icon
                      name={getTypeIcon(item.type) as any}
                      size={14}
                      className="mr-1"
                    />
                    {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-lg font-headline font-bold text-textPrimary mb-2 line-clamp-2">
                  {item.title}
                </h3>

                <div className="flex items-center text-sm text-textSecondary mb-3">
                  <Icon name="BuildingOfficeIcon" size={16} className="mr-2" />
                  <span className="mr-4">{item.source}</span>
                  <Icon name="CalendarIcon" size={16} className="mr-2" />
                  <span>{item.date}</span>
                </div>

                <p className="text-sm text-textSecondary leading-relaxed line-clamp-3">
                  {item.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}